import { atom } from 'nanostores';

const MAX_TERMINAL_LINES = 5000;

export interface TerminalLine {
  text: string;
  type: 'stdout' | 'stderr' | 'info';
}

export const terminalStore = {
  lines: atom<TerminalLine[]>([]),
  isRunning: atom<boolean>(false),
};

/**
 * Strip ANSI enough for spinner / progress heuristics (avoid importing build.ts).
 */
function stripAnsiForDetect(s: string): string {
  return s
    .replace(/\u001b\[[0-9;]*[A-Za-z]/g, '')
    .replace(/\u001b\][^\u0007\u001b]*(?:\u0007|\u001b\\)/g, '')
    .replace(/\u001b[^[\\]]/g, '');
}

/**
 * npm / CLI ASCII spinners (`\\|/-`) concatenate into one noise line when CR redraws collapse.
 * Removes lone glyphs and lines made only of spinner characters.
 */
function sanitizeAsciiSpinnerNoise(line: string): string | null {
  let t = stripAnsiForDetect(line).trimEnd();
  if (!t.trim()) return null;

  t = t.replace(/(?:[\|\\/\-]){8,}/g, '').trim();
  if (!t) return null;

  const trimmed = t.trim();
  if (trimmed.length === 1 && /^[\|\\/\-]$/.test(trimmed)) return null;
  if (trimmed.length >= 2 && /^[\|\\/\-]+$/.test(trimmed)) return null;

  /** Braille/dot spinner rows collapsed onto one line */
  if (
    trimmed.length >= 2 &&
    /^[⠋⠙⠹⠸⠼⠴⠦⠧⠇⠏⣾⣽⣻⢿⡿⣟⣯⣷]+$/.test(trimmed)
  ) {
    return null;
  }

  return t;
}

/** npm / ora-style spinners: one drawn character per frame, sometimes without `\\r`. */
function isShortSpinnerLine(s: string): boolean {
  const t = stripAnsiForDetect(s).trimEnd();
  if (!t) return false;
  if (t.length === 1 && /^[\-|/\\]$/.test(t)) return true;
  /** Common 1-cell braille / dot spinners */
  if (/^[⠋⠙⠹⠸⠼⠴⠦⠧⠇⠏⣾⣽⣻⢿⡿⣟⣯⣷]$/.test(t)) return true;
  return false;
}

/**
 * Append process output with TTY-style newline and carriage-return handling.
 * npm/cli spinners and progress bars use `\\r` to redraw one line; naive per-chunk
 * rows show every frame and huge gaps. Coalesce into the last row of the same stream.
 */
function appendStreamChunk(text: string, type: 'stdout' | 'stderr'): void {
  let lines = [...terminalStore.lines.get()];
  let work = '';
  if (lines.length > 0 && lines[lines.length - 1]?.type === type) {
    work = lines.pop()!.text;
  }

  // Separate writes of `-`, `\`, `|`, `/` with no CR/LF — merge into one logical row.
  if (!/[\r\n]/.test(text) && isShortSpinnerLine(text) && isShortSpinnerLine(work)) {
    work = text;
    lines.push({ text: work, type });
    if (lines.length > MAX_TERMINAL_LINES) {
      lines = lines.slice(lines.length - MAX_TERMINAL_LINES);
    }
    terminalStore.lines.set(lines);
    return;
  }

  for (let i = 0; i < text.length; i++) {
    const c = text[i]!;
    if (c === '\r') {
      work = '';
    } else if (c === '\n') {
      if (
        isShortSpinnerLine(work) &&
        lines.length > 0 &&
        lines[lines.length - 1]?.type === type &&
        isShortSpinnerLine(lines[lines.length - 1]!.text)
      ) {
        lines[lines.length - 1] = { text: work, type };
      } else {
        lines.push({ text: work, type });
      }
      work = '';
    } else {
      work += c;
    }
  }
  lines.push({ text: work, type });
  if (lines.length > MAX_TERMINAL_LINES) {
    lines = lines.slice(lines.length - MAX_TERMINAL_LINES);
  }
  terminalStore.lines.set(lines);
}

export function appendTerminalLine(text: string, type: TerminalLine['type'] = 'stdout'): void {
  if (type === 'info') {
    let current = [...terminalStore.lines.get(), { text, type: 'info' as const }];
    if (current.length > MAX_TERMINAL_LINES) {
      current = current.slice(current.length - MAX_TERMINAL_LINES);
    }
    terminalStore.lines.set(current);
    return;
  }
  appendStreamChunk(text, type);
}

/**
 * Append a subprocess's **complete** captured output as readable lines.
 * Streaming PTY output through {@link appendTerminalLine} loses text whenever tools use `\\r`
 * redraws (spinners/progress); flushing once after exit collapses redraws to final lines.
 */
export function appendTerminalProcessOutput(raw: string, type: 'stdout' | 'stderr' = 'stdout'): void {
  if (!raw.trim()) return;

  const rows: string[] = [];
  for (const segment of raw.replace(/\r\n/g, '\n').split('\n')) {
    const afterCr = segment.split('\r').pop() ?? '';
    const cleaned = sanitizeAsciiSpinnerNoise(afterCr);
    if (cleaned !== null) rows.push(cleaned);
  }

  const normalized = rows.join('\n');
  if (!normalized.trim()) return;
  appendStreamChunk(normalized, type);
}

export function clearTerminal(): void {
  terminalStore.lines.set([]);
}
